import type {
  FsdCompanyURN,
  FsdProfileURN,
  HashtagURN,
  MemberURN,
  OrganizationURN,
} from '@perfectpost/linkedin-privateapi-ts-client';

export interface CheckInstall {
  source: 'perfectpost';
  type: 'check_install';
}

export interface ReloadSession {
  source: 'perfectpost';
  type: 'reload_session';
}

export interface CheckQueue {
  source: 'perfectpost';
  type: 'check_queue';
}

export interface LoginOnly {
  source: 'perfectpost';
  type: 'login_only';
  payload: {email: string; password: string};
}

export interface Logout {
  source: 'perfectpost';
  type: 'logout';
}

export interface SearchTypeahead {
  source: 'perfectpost';
  type: 'search_typeahead';
  searchKeyword: string;
  queryId: string;
}

export interface ResolveProfileMention {
  source: 'perfectpost';
  type: 'resolve_profile_mention';
  entityUrn: string;
}

export function isSearchTypeaheadEvent(object: unknown): object is SearchTypeahead {
  if (typeof object !== 'object' || object === null) return false;
  const candidate = object as Partial<SearchTypeahead>;
  return (
    candidate.type === 'search_typeahead' &&
    typeof candidate.searchKeyword === 'string' &&
    typeof candidate.queryId === 'string'
  );
}

export function isResolveProfileMentionEvent(object: unknown): object is ResolveProfileMention {
  if (typeof object !== 'object' || object === null) {
    return false;
  }

  const candidate = object as {source?: unknown; type?: unknown; entityUrn?: unknown; requestId?: unknown};
  if (candidate.source !== 'perfectpost') {
    return false;
  }

  return (
    candidate.source === 'perfectpost' &&
    candidate.type === 'resolve_profile_mention' &&
    typeof candidate.entityUrn === 'string'
  );
}

export interface OpenPopup {
  source: 'perfectpost';
  type: 'open_popup';
}

export type PerfectPostMessage =
  | CheckInstall
  | ReloadSession
  | CheckQueue
  | LoginOnly
  | Logout
  | SearchTypeahead
  | ResolveProfileMention
  | OpenPopup;
export function isPerfectPostMessage(object: unknown): object is PerfectPostMessage {
  return (
    object !== undefined &&
    object !== null &&
    typeof object === 'object' &&
    'source' in object &&
    object?.source !== undefined &&
    object.source === 'perfectpost' &&
    'type' in object &&
    object?.type !== undefined &&
    (object.type === 'check_install' ||
      object.type === 'reload_session' ||
      object.type === 'check_queue' ||
      object.type === 'login_only' ||
      object.type === 'logout' ||
      object.type === 'search_typeahead' ||
      object.type === 'resolve_profile_mention' ||
      object.type === 'open_popup')
  );
}

interface PerfectPostMessageResponseBase {
  source: 'perfectpost';
  type: `${PerfectPostMessage['type']}_response`;
  payload?: unknown;
}

export type SearchTypeaheadResponse =
  | ({
      type: 'search_typeahead_response';
      status: 'success';
      error?: undefined;
      code?: undefined;
      payload: Array<{
        entityUrn: FsdProfileURN | FsdCompanyURN;
        picture?: string;
        value: string;
        objectUrn: HashtagURN | MemberURN | OrganizationURN | FsdCompanyURN;
      }>;
    } & PerfectPostMessageResponseBase)
  | ({
      type: 'search_typeahead_response';
      status: 'error';
      error: string;
      code?: 'UNAUTHENTICATED' | 'CSRF' | 'LINKEDIN' | 'UNEXPECTED';
      payload?: undefined;
    } & PerfectPostMessageResponseBase);

export type ResolveProfileMentionResponse =
  | ({
      type: 'resolve_profile_mention_response';
      status: 'success';
      error?: undefined;
      code?: undefined;
      payload: {
        entityUrn: FsdProfileURN;
        objectUrn: MemberURN;
        vanityName?: string;
        // Absents des extensions antérieures à 26.10.3 : un consommateur doit savoir s'en passer.
        firstName?: string;
        lastName?: string;
        /** URL de la photo de profil (~100 px), absente si la personne n'en a pas. */
        picture?: string;
      };
    } & PerfectPostMessageResponseBase)
  | ({
      type: 'resolve_profile_mention_response';
      status: 'error';
      error: string;
      code?: 'UNAUTHENTICATED' | 'CSRF' | 'LINKEDIN' | 'UNEXPECTED';
      payload?: undefined;
    } & PerfectPostMessageResponseBase);

interface CheckInstallResponsePayload {
  csrfTokenExists: boolean;
  extensionVersion: string;
  isAuthenticated: boolean;
  perfectPostEmail?: string;
  perfectPostId?: string;
  linkedInVanity?: string;
  linkedInEntityUrn?: string;
  accountMismatch: boolean;
  missingAccountAssociation: boolean;
  accountStatus: 'ok' | 'mismatch' | 'missing_association' | 'no_session';
  accountMismatchDetails?: {
    sessionVanity?: string;
    sessionEntityUrn?: string;
    currentVanity?: string;
    currentEntityUrn?: string;
  };
  lastVueUpdateAt?: Date;
  lastCommentFetch?: Date;
  lastFollowerUpdateAt?: Date;
  lastRelationUpdateAt?: Date;
  lastScheduledPostUpdateAt?: Date;
  lastActivityUpdateAt?: Date;
  lastPostUpdateAt?: Date;
  lastExtensionPingAt?: Date;
  nextVueUpdateAt?: Date;
  nextFollowerUpdateAt?: Date;
  nextRelationUpdateAt?: Date;
  nextScheduledPostUpdateAt?: Date;
  nextActivityUpdateAt?: Date;
  nextPostUpdateAt?: Date;
}
export interface CheckInstallResponse extends PerfectPostMessageResponseBase {
  type: 'check_install_response';
  payload: CheckInstallResponsePayload;
}

export interface CheckQueueResponse extends PerfectPostMessageResponseBase {
  type: 'check_queue_response';
}

export interface ReloadSessionResponse extends PerfectPostMessageResponseBase {
  type: 'reload_session_response';
  payload?: LoginSuccessResult;
}

export type LoginSuccessResult =
  | {
      authenticated: true;
      premium: boolean;
      trialEndAt: string | undefined;
      objectifLike: number;
      objectifComment: number;
      lnAccessTokenValid: boolean;
    }
  | {authenticated: true; error: 'MISSING_ACCOUNT_ASSOCIATION'}
  // `entityUrn` : le champ sur lequel le refus est décidé. Il voyage avec le résultat pour que
  // les écrans montrent le profil qui a servi à la décision, et non le `vanityName` qui, lui,
  // peut être identique des deux côtés. Optionnel : une association faite avant l'enregistrement
  // de l'`entityUrn` n'en a pas.
  | {authenticated: true; error: 'ACCOUNT_MISMATCH'; vanityName: string; entityUrn?: string}
  | {challengeName: 'NEW_PASSWORD_REQUIRED'};

export interface PremiumStatusChangeEvent {
  source: 'perfectpost';
  type: 'premium_status_change';
  payload: LoginSuccessResult;
}

export function isPremiumStatusChangeEvent(object: unknown): object is PremiumStatusChangeEvent {
  return (
    object !== null &&
    typeof object === 'object' &&
    'source' in object &&
    object.source === 'perfectpost' &&
    'type' in object &&
    object.type === 'premium_status_change' &&
    'payload' in object &&
    object.payload !== null &&
    typeof object.payload === 'object'
  );
}

export interface LoginResponse extends PerfectPostMessageResponseBase {
  type: 'login_only_response';
  payload: LoginSuccessResult;
}

export interface LogoutResponse extends PerfectPostMessageResponseBase {
  type: 'logout_response';
}

export interface OpenPopupResponse extends PerfectPostMessageResponseBase {
  type: 'open_popup_response';
  payload: {opened: boolean; reason?: 'unsupported' | 'no_gesture' | 'error'};
}

export interface ExtensionErrorResponse {
  source: 'perfectpost';
  type: 'error_response';
  payload: {
    originalType: string;
    error: string;
  };
}

export type PerfectPostMessageResponse =
  | CheckInstallResponse
  | CheckQueueResponse
  | ReloadSessionResponse
  | LoginResponse
  | LogoutResponse
  | SearchTypeaheadResponse
  | ResolveProfileMentionResponse
  | OpenPopupResponse
  | ExtensionErrorResponse;

export function isPerfectPostMessageResponse(object: unknown): object is PerfectPostMessageResponse {
  if (object !== undefined && object !== null && typeof object === 'object' && 'source' in object && 'type' in object) {
    const o = object as {source?: unknown; type?: unknown};
    return (
      o.source === 'perfectpost' &&
      typeof o.type === 'string' &&
      [
        'check_install_response',
        'check_queue_response',
        'reload_session_response',
        'login_only_response',
        'logout_response',
        'search_typeahead_response',
        'resolve_profile_mention_response',
        'open_popup_response',
        'error_response',
      ].includes(o.type)
    );
  }
  return false;
}

export interface MessageResponseMap {
  check_install: CheckInstallResponse;
  reload_session: ReloadSessionResponse;
  check_queue: CheckQueueResponse;
  login_only: LoginResponse;
  logout: LogoutResponse;
  search_typeahead: SearchTypeaheadResponse;
  resolve_profile_mention: ResolveProfileMentionResponse;
  open_popup: OpenPopupResponse;
}
